import {
  PermissionCode,
  WEBHOOK_DIRECTION_LABELS,
  WEBHOOK_EVENT_LABELS,
  WEBHOOK_EVENTS,
  WEBHOOK_FIELD_TARGET_LABELS,
  WEBHOOK_FIELD_TARGETS,
  WebhookDirection,
  WebhookEvent,
  upsertWebhookEndpointRequestSchema,
  type UpsertWebhookEndpointRequest,
  type WebhookDirection as WebhookDirectionCode,
  type WebhookEvent as WebhookEventCode,
  type WebhookFieldMapping,
} from '@gb-ticket/shared-types';
import { Loader2Icon, PlusIcon, Trash2Icon } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useMailConnection } from '@/platform/features/mail/use-mail-connection';
import { MAIL_ADMIN_PATH } from '@/platform/lib/admin';
import {
  useCreateWebhook,
  useDeleteWebhook,
  useIntegrations,
  useRotateWebhook,
  useSyncEntraNow,
  useSyncLdapNow,
  useUpdateLdapIntegration,
  useUpdateWebhook,
} from '@/products/helpdesk/features/integrations/use-integrations';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

type TabId = 'mail' | 'ldap' | 'entra' | 'webhooks';

type MappingDraft = {
  source: string;
  target: string;
  transform: string;
};

function emptyMapping(): MappingDraft {
  return { source: '$.ticket.subject', target: 'subject', transform: '' };
}

function parseMappings(rows: MappingDraft[]): WebhookFieldMapping[] {
  return rows
    .filter((row) => row.source.trim() && row.target.trim())
    .map((row) => ({
      source: row.source.trim(),
      target: row.target.trim(),
      ...(row.transform === 'trim' ||
      row.transform === 'upper' ||
      row.transform === 'lower' ||
      row.transform === 'map'
        ? { transform: row.transform }
        : {}),
    }));
}

export function IntegrationsPage() {
  const query = useIntegrations();
  const [tab, setTab] = useState<TabId>('mail');
  const [revealed, setRevealed] = useState<{ title: string; lines: string[] } | null>(null);

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Integrationen</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Microsoft-365-Verzeichnis, optionales LDAP/AD und generische Webhooks. Das Postfach
          richtet die IT-Administration unter Verwaltung ein.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {(
          [
            ['mail', 'E-Mail-Eingang'],
            ['entra', 'Microsoft 365'],
            ['ldap', 'LDAP / Active Directory'],
            ['webhooks', 'Webhooks'],
          ] as const
        ).map(([id, label]) => (
          <Button
            key={id}
            type="button"
            variant={tab === id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTab(id)}
          >
            {label}
          </Button>
        ))}
      </div>

      {query.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Integrationen werden geladen …
        </p>
      )}
      {query.isError && (
        <p className="text-sm text-muted-foreground">
          {query.error instanceof ApiRequestError
            ? query.error.message
            : 'Integrationen konnten nicht geladen werden.'}
        </p>
      )}

      {tab === 'mail' && <MailCard />}
      {query.data && tab === 'entra' && <EntraCard entra={query.data.entra} />}
      {query.data && tab === 'ldap' && <LdapCard ldap={query.data.ldap} />}
      {query.data && tab === 'webhooks' && (
        <WebhooksCard
          endpoints={query.data.webhooks}
          onSecrets={(title, lines) => setRevealed({ title, lines })}
        />
      )}

      {revealed && (
        <Card>
          <CardHeader>
            <CardTitle>{revealed.title}</CardTitle>
            <CardDescription>
              Bitte jetzt kopieren — der Wert wird nicht erneut angezeigt.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {revealed.lines.map((line) => (
              <p key={line} className="font-mono text-sm break-all">
                {line}
              </p>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setRevealed(null)}>
              Schließen
            </Button>
          </CardContent>
        </Card>
      )}
    </section>
  );
}

const INBOUND_LABELS = {
  off: 'Aus',
  microsoft_graph: 'Microsoft 365',
  imap: 'IMAP',
} as const;

function MailCard() {
  const me = useMe();
  const canManageMail = hasPermission(me.data, PermissionCode.MAIL_MANAGE);
  const connection = useMailConnection(canManageMail);

  return (
    <Card>
      <CardHeader>
        <CardTitle>E-Mail-Eingang</CardTitle>
        <CardDescription>
          Eingehende Mails an die Support-Adresse werden zu Tickets; Antworten im Thread werden als
          öffentliche Kommentare zugeordnet.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {!canManageMail && (
          <p className="text-muted-foreground">
            Das Postfach richtet die IT-Administration unter Verwaltung ein.
          </p>
        )}
        {canManageMail && connection.isPending && (
          <p className="flex items-center gap-2 text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Stand wird geladen …
          </p>
        )}
        {canManageMail && connection.isError && (
          <p className="text-muted-foreground">
            {connection.error instanceof ApiRequestError
              ? connection.error.message
              : 'Der Stand konnte nicht geladen werden.'}
          </p>
        )}
        {canManageMail && connection.data && (
          <>
            <p>
              Art:{' '}
              <span className="font-medium">{INBOUND_LABELS[connection.data.inboundMode]}</span>
              {' · '}
              Letzter Abruf:{' '}
              {connection.data.lastInboundAt
                ? formatDateTime(connection.data.lastInboundAt)
                : 'noch nie'}
              {connection.data.lastInboundError
                ? ` · Fehler: ${connection.data.lastInboundError}`
                : ''}
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to={MAIL_ADMIN_PATH}>In der Verwaltung öffnen</Link>
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function EntraCard({
  entra,
}: {
  entra: NonNullable<ReturnType<typeof useIntegrations>['data']>['entra'];
}) {
  const sync = useSyncEntraNow();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Microsoft 365 Verzeichnis</CardTitle>
        <CardDescription>
          Stündlicher Graph-Abgleich der Benutzer (Namen, E-Mail, Aktiv-Status) und der
          IT-Gruppenrollen. Anmeldung bleibt OpenID Connect; E-Mail-Eingang nutzt weiterhin{' '}
          <code>GRAPH_MAIL_*</code>, nicht diese Zugangsdaten.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Badge variant={entra.envConfigured ? 'default' : 'secondary'}>
          {entra.envConfigured ? 'ENTRA_* konfiguriert' : 'ENTRA_* fehlt'}
        </Badge>
        <dl className="grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
          <div>
            Letzter Sync: {entra.lastSyncAt ? formatDateTime(entra.lastSyncAt) : 'noch nie'}
          </div>
          <div>
            Angelegt {entra.lastSyncCreated} · aktualisiert {entra.lastSyncUpdated} · deaktiviert{' '}
            {entra.lastSyncDeactivated}
          </div>
          {entra.lastError && (
            <div className="text-destructive sm:col-span-2">Fehler: {entra.lastError}</div>
          )}
        </dl>
        <Button
          type="button"
          variant="outline"
          disabled={sync.isPending || !entra.envConfigured}
          onClick={() => {
            void sync.mutateAsync().then(
              () => toast.success('Microsoft-365-Sync eingereiht.'),
              (error: unknown) =>
                toast.error(
                  error instanceof ApiRequestError ? error.message : 'Sync fehlgeschlagen.',
                ),
            );
          }}
        >
          {sync.isPending && <Loader2Icon className="size-4 animate-spin" />}
          Jetzt synchronisieren
        </Button>
      </CardContent>
    </Card>
  );
}

function LdapCard({
  ldap,
}: {
  ldap: NonNullable<ReturnType<typeof useIntegrations>['data']>['ldap'];
}) {
  const save = useUpdateLdapIntegration();
  const sync = useSyncLdapNow();
  const [isActive, setIsActive] = useState(ldap.isActive);
  const [deactivateMissing, setDeactivateMissing] = useState(ldap.deactivateMissing);

  async function onSave(event: FormEvent) {
    event.preventDefault();
    try {
      await save.mutateAsync({ isActive, deactivateMissing });
      toast.success('LDAP-Einstellungen gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>LDAP / Active Directory</CardTitle>
        <CardDescription>
          Optional. Nutzer ohne lokales Passwort können sich per LDAP anmelden (Just-in-time-Anlage
          als Endnutzer). Zusätzlich <code>LDAP_ENABLED=true</code> in der Umgebung. Entra-SSO ist
          davon unabhängig.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={(event) => void onSave(event)}>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={isActive}
                onCheckedChange={(value) => setIsActive(value === true)}
              />
              Aktiv
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={deactivateMissing}
                onCheckedChange={(value) => setDeactivateMissing(value === true)}
              />
              Fehlende LDAP-Konten deaktivieren
            </label>
            <Badge variant={ldap.envConfigured ? 'default' : 'secondary'}>
              {ldap.envConfigured ? 'Verbindung konfiguriert' : 'LDAP_URL fehlt'}
            </Badge>
          </div>
          <dl className="grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
            <div>
              Letzter Sync: {ldap.lastSyncAt ? formatDateTime(ldap.lastSyncAt) : 'noch nie'}
            </div>
            <div>
              Angelegt {ldap.lastSyncCreated} · aktualisiert {ldap.lastSyncUpdated} · deaktiviert{' '}
              {ldap.lastSyncDeactivated}
            </div>
            {ldap.lastError && (
              <div className="text-destructive sm:col-span-2">Fehler: {ldap.lastError}</div>
            )}
          </dl>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending && <Loader2Icon className="size-4 animate-spin" />}
              Speichern
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={sync.isPending}
              onClick={() => {
                void sync.mutateAsync().then(
                  () => toast.success('LDAP-Sync eingereiht.'),
                  (error: unknown) =>
                    toast.error(
                      error instanceof ApiRequestError ? error.message : 'Sync fehlgeschlagen.',
                    ),
                );
              }}
            >
              {sync.isPending && <Loader2Icon className="size-4 animate-spin" />}
              Jetzt synchronisieren
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function WebhooksCard({
  endpoints,
  onSecrets,
}: {
  endpoints: NonNullable<ReturnType<typeof useIntegrations>['data']>['webhooks'];
  onSecrets: (title: string, lines: string[]) => void;
}) {
  const create = useCreateWebhook();
  const update = useUpdateWebhook();
  const rotate = useRotateWebhook();
  const remove = useDeleteWebhook();
  const [name, setName] = useState('Streit V1');
  const [direction, setDirection] = useState<WebhookDirectionCode>(WebhookDirection.OUTBOUND);
  const [url, setUrl] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [events, setEvents] = useState<WebhookEventCode[]>([WebhookEvent.TICKET_CREATED]);
  const [mappings, setMappings] = useState<MappingDraft[]>([emptyMapping()]);
  const [editingId, setEditingId] = useState<string | null>(null);

  const draft = useMemo<UpsertWebhookEndpointRequest>(
    () => ({
      name,
      isActive,
      direction,
      url: direction === WebhookDirection.OUTBOUND && url.trim() ? url.trim() : null,
      events,
      fieldMappings: parseMappings(mappings),
    }),
    [name, isActive, direction, url, events, mappings],
  );

  function toggleEvent(event: WebhookEventCode, checked: boolean) {
    setEvents((current) =>
      checked ? [...new Set([...current, event])] : current.filter((item) => item !== event),
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const parsed = upsertWebhookEndpointRequestSchema.safeParse(draft);
    if (!parsed.success) {
      toast.error('Bitte Name, URL (ausgehend) und mindestens gültige Feldzuordnungen prüfen.');
      return;
    }
    try {
      if (editingId) {
        await update.mutateAsync({ id: editingId, body: parsed.data });
        toast.success('Webhook gespeichert.');
        setEditingId(null);
      } else {
        const created = await create.mutateAsync(parsed.data);
        const lines = [
          created.secrets.signingSecret ? `Signatur-Secret: ${created.secrets.signingSecret}` : '',
          created.secrets.inboundUrl ? `Inbound-URL: ${created.secrets.inboundUrl}` : '',
          created.secrets.inboundToken ? `Token: ${created.secrets.inboundToken}` : '',
        ].filter(Boolean);
        onSecrets('Zugangsdaten (einmalig)', lines);
        toast.success('Webhook angelegt.');
      }
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Webhook konnte nicht gespeichert werden.',
      );
    }
  }

  function loadForEdit(id: string) {
    const row = endpoints.find((item) => item.id === id);
    if (!row) {
      return;
    }
    setEditingId(row.id);
    setName(row.name);
    setDirection(row.direction);
    setUrl(row.url ?? '');
    setIsActive(row.isActive);
    setEvents(row.events);
    setMappings(
      row.fieldMappings.length > 0
        ? row.fieldMappings.map((item) => ({
            source: item.source,
            target: item.target,
            transform: item.transform ?? '',
          }))
        : [emptyMapping()],
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{editingId ? 'Webhook bearbeiten' : 'Webhook anlegen'}</CardTitle>
          <CardDescription>
            Ausgehend: Das Mitarbeiterportal sendet Ereignisse (HMAC-Signatur im Header{' '}
            <code>X-GB-Ticket-Signature</code>). Eingehend: ERP postet auf eine Token-URL.
            Feldmapping analog Export-Modulen (JSON-Pfad → Zielfeld).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={(event) => void onSubmit(event)}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="wh-name">Name</Label>
                <Input
                  id="wh-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="wh-dir">Richtung</Label>
                <Select
                  value={direction}
                  onValueChange={(value) => setDirection(value as WebhookDirectionCode)}
                  disabled={Boolean(editingId)}
                >
                  <SelectTrigger id="wh-dir">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(WebhookDirection).map((code) => (
                      <SelectItem key={code} value={code}>
                        {WEBHOOK_DIRECTION_LABELS[code]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {direction === WebhookDirection.OUTBOUND && (
              <div className="space-y-1">
                <Label htmlFor="wh-url">Ziel-URL</Label>
                <Input
                  id="wh-url"
                  type="url"
                  placeholder="https://erp.example.invalid/hooks/tickets"
                  value={url}
                  onChange={(event) => setUrl(event.target.value)}
                />
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={isActive}
                onCheckedChange={(value) => setIsActive(value === true)}
              />
              Aktiv
            </label>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Ereignisse</legend>
              <div className="flex flex-wrap gap-3">
                {WEBHOOK_EVENTS.map((event) => (
                  <label key={event} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={events.includes(event)}
                      onCheckedChange={(value) => toggleEvent(event, value === true)}
                    />
                    {WEBHOOK_EVENT_LABELS[event]}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Feldmapping</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setMappings((rows) => [...rows, emptyMapping()])}
                >
                  <PlusIcon className="size-4" />
                  Zeile
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Quelle z. B. <code>$.ticket.subject</code> (ausgehend) oder{' '}
                <code>bestellung.titel</code> (eingehend). Ziel: ERP-Feld bzw.{' '}
                {WEBHOOK_FIELD_TARGETS.map((item) => WEBHOOK_FIELD_TARGET_LABELS[item]).join(', ')}.
              </p>
              {mappings.map((row, index) => (
                <div
                  key={`${row.target}-${index}`}
                  className="grid gap-2 sm:grid-cols-[1fr_1fr_8rem_auto]"
                >
                  <Input
                    aria-label="Quelle"
                    placeholder="$.ticket.subject"
                    value={row.source}
                    onChange={(event) =>
                      setMappings((rows) =>
                        rows.map((item, i) =>
                          i === index ? { ...item, source: event.target.value } : item,
                        ),
                      )
                    }
                  />
                  <Input
                    aria-label="Ziel"
                    placeholder="subject"
                    value={row.target}
                    onChange={(event) =>
                      setMappings((rows) =>
                        rows.map((item, i) =>
                          i === index ? { ...item, target: event.target.value } : item,
                        ),
                      )
                    }
                  />
                  <Select
                    value={row.transform || 'none'}
                    onValueChange={(value) =>
                      setMappings((rows) =>
                        rows.map((item, i) =>
                          i === index
                            ? { ...item, transform: value === 'none' ? '' : value }
                            : item,
                        ),
                      )
                    }
                  >
                    <SelectTrigger aria-label="Transformation">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">keine</SelectItem>
                      <SelectItem value="trim">trim</SelectItem>
                      <SelectItem value="upper">upper</SelectItem>
                      <SelectItem value="lower">lower</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Zeile entfernen"
                    onClick={() => setMappings((rows) => rows.filter((_, i) => i !== index))}
                  >
                    <Trash2Icon className="size-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={create.isPending || update.isPending}>
                {(create.isPending || update.isPending) && (
                  <Loader2Icon className="size-4 animate-spin" />
                )}
                {editingId ? 'Änderungen speichern' : 'Anlegen'}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" onClick={() => setEditingId(null)}>
                  Abbrechen
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vorhandene Webhooks</CardTitle>
        </CardHeader>
        <CardContent>
          {endpoints.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Webhooks.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Richtung</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Zuletzt</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {endpoints.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="font-medium">{row.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.events.join(', ') || 'keine Ereignisse'}
                      </div>
                    </TableCell>
                    <TableCell>{WEBHOOK_DIRECTION_LABELS[row.direction]}</TableCell>
                    <TableCell>
                      {row.isActive ? 'aktiv' : 'inaktiv'}
                      {row.lastStatus != null ? ` · HTTP ${row.lastStatus}` : ''}
                      {row.lastError ? ` · ${row.lastError}` : ''}
                    </TableCell>
                    <TableCell>{row.lastFiredAt ? formatDateTime(row.lastFiredAt) : '—'}</TableCell>
                    <TableCell className="space-x-2 text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => loadForEdit(row.id)}
                      >
                        Bearbeiten
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={rotate.isPending}
                        onClick={() => {
                          void rotate.mutateAsync(row.id).then(
                            (secrets) => {
                              onSecrets(
                                'Neue Zugangsdaten (einmalig)',
                                [
                                  secrets.signingSecret
                                    ? `Signatur-Secret: ${secrets.signingSecret}`
                                    : '',
                                  secrets.inboundUrl ? `Inbound-URL: ${secrets.inboundUrl}` : '',
                                ].filter(Boolean),
                              );
                            },
                            (error: unknown) =>
                              toast.error(
                                error instanceof ApiRequestError
                                  ? error.message
                                  : 'Rotation fehlgeschlagen.',
                              ),
                          );
                        }}
                      >
                        Secret rotieren
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button type="button" variant="destructive" size="sm">
                            Löschen
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Webhook löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {row.name} wird entfernt. Ausstehende Zustellungen können
                              fehlschlagen.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => {
                                void remove.mutateAsync(row.id).then(
                                  () => toast.success('Webhook gelöscht.'),
                                  (error: unknown) =>
                                    toast.error(
                                      error instanceof ApiRequestError
                                        ? error.message
                                        : 'Löschen fehlgeschlagen.',
                                    ),
                                );
                              }}
                            >
                              Löschen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
